import { useEffect, useMemo, useRef, useState } from "react";
import { Loader2 } from "lucide-react";

const CONF = ["#C9A96A", "#F2D9A6", "#0B6E8F", "#34B3B0", "#E0533D", "#ffffff"];

export function Confetti({ n = 90 }) {
  const bits = useMemo(() => Array.from({ length: n }, (_, i) => ({
    left: Math.random() * 100, w: 6 + Math.random() * 6, h: 8 + Math.random() * 10, c: CONF[i % CONF.length],
    dx: `${Math.random() * 120 - 60}px`, rot: `${Math.random() * 900 - 450}deg`, dur: `${2.6 + Math.random() * 2}s`, delay: `${Math.random() * 0.9}s`, round: i % 4 === 0,
  })), [n]);
  return (
    <div className="pointer-events-none fixed inset-0 z-50 overflow-hidden" data-testid="confetti">
      {bits.map((b, i) => (
        <span key={i} className="confetti-piece" style={{ left: `${b.left}%`, width: b.w, height: b.round ? b.w : b.h, background: b.c, borderRadius: b.round ? "50%" : 2, "--dx": b.dx, "--rot": b.rot, "--dur": b.dur, "--delay": b.delay }} />
      ))}
    </div>
  );
}

function paintCover(cv, label) {
  const ctx = cv.getContext("2d");
  const { width: w, height: h } = cv;
  const g = ctx.createLinearGradient(0, 0, w, h);
  ["#A8854A", "#E9CF95", "#C9A96A", "#F7E7BF", "#B8945A"].forEach((c, i) => g.addColorStop(i / 4, c));
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, w, h);
  ctx.fillStyle = "rgba(255,255,255,0.18)";
  for (let i = 0; i < 260; i++) ctx.fillRect(Math.random() * w, Math.random() * h, 2, 2);
  ctx.fillStyle = "#5A4320";
  ctx.textAlign = "center";
  ctx.font = `600 ${Math.round(w / 13)}px 'Plus Jakarta Sans', sans-serif`;
  ctx.fillText(label, w / 2, h / 2 + w / 40);
  ctx.font = `${Math.round(w / 9)}px serif`;
  ctx.fillText("✦", w / 2, h / 2 - w / 9);
}

export function ScratchCard({ prize, onDone, t }) {
  const ref = useRef(null);
  const [gone, setGone] = useState(false);
  const moves = useRef(0);
  useEffect(() => {
    const cv = ref.current;
    const dpr = window.devicePixelRatio || 1;
    cv.width = cv.offsetWidth * dpr;
    cv.height = cv.offsetHeight * dpr;
    paintCover(cv, t("GRATTA QUI", "SCRATCH HERE"));
  }, [t]);
  const cleared = () => {
    const cv = ref.current;
    const d = cv.getContext("2d").getImageData(0, 0, cv.width, cv.height).data;
    let clear = 0, tot = 0;
    for (let i = 3; i < d.length; i += 4 * 40) { tot++; if (d[i] === 0) clear++; }
    return clear / tot;
  };
  const scratch = (e) => {
    if (!prize || gone || !(e.buttons || e.pointerType === "touch")) return;
    const cv = ref.current;
    const r = cv.getBoundingClientRect();
    const k = cv.width / r.width;
    const ctx = cv.getContext("2d");
    ctx.globalCompositeOperation = "destination-out";
    ctx.beginPath();
    ctx.arc((e.clientX - r.left) * k, (e.clientY - r.top) * k, 24 * k, 0, Math.PI * 2);
    ctx.fill();
    if (++moves.current % 8 === 0 && cleared() > 0.5) {
      setGone(true);
      setTimeout(onDone, 2200);
    }
  };
  return (
    <div className="relative w-full max-w-[340px] aspect-[16/10] mx-auto rounded-2xl overflow-hidden shadow-2xl ring-4 ring-[#C9A96A]/60 glow-pulse select-none" data-testid="scratch-card">
      <div className="absolute inset-0 bg-gradient-to-br from-[#0D2B3A] to-[#0B6E8F] text-white flex flex-col items-center justify-center p-5 text-center">
        <div className="text-[10px] uppercase tracking-[0.35em] text-[#F2D9A6]">{t("Hai vinto", "You won")}</div>
        {prize ? <div className="font-serif-it text-3xl font-semibold mt-1 leading-tight" data-testid="scratch-prize">{prize.nome}</div> : <Loader2 className="animate-spin mt-2" />}
      </div>
      <canvas ref={ref} onPointerDown={scratch} onPointerMove={scratch} className={`absolute inset-0 w-full h-full touch-none cursor-grab transition-opacity duration-700 ${gone ? "opacity-0 pointer-events-none" : ""}`} data-testid="scratch-canvas" />
    </div>
  );
}

const BOX = [["#0B6E8F", "#C9A96A"], ["#E0533D", "#F2D9A6"], ["#34B3B0", "#ffffff"]];

function Gift({ i, state, label, onPick }) {
  const [body, ribbon] = BOX[i];
  const picked = state === "picked";
  return (
    <button type="button" onClick={onPick} disabled={state !== "idle"} data-testid={`gift-box-${i}`}
      className={`relative w-[92px] sm:w-[110px] h-[120px] sm:h-[140px] flex items-end justify-center ${state === "idle" ? "gift-idle" : picked ? "gift-shake" : "opacity-60"}`}
      style={{ animationDelay: state === "idle" ? `${i * 0.3}s` : undefined }}>
      {label && (
        <div className="absolute inset-x-0 bottom-16 z-20 flex justify-center">
          <div className={`w-[88px] sm:w-[104px] rounded-lg px-2 py-1.5 text-[11px] font-semibold leading-tight shadow-lg ${picked ? "gift-prize-pop bg-white text-[#0B6E8F] ring-2 ring-[#C9A96A]" : "fade-up bg-white/90 text-[#47606B]"}`} style={picked ? undefined : { animationDelay: "1.6s" }}>{label}</div>
        </div>
      )}
      <div className="relative w-full h-[72%] rounded-md shadow-xl" style={{ background: body }}>
        <div className="absolute inset-y-0 left-1/2 -translate-x-1/2 w-3" style={{ background: ribbon }} />
      </div>
      <div className={`absolute bottom-[68%] w-[112%] h-[22%] rounded-md shadow-md z-10 ${picked ? "gift-lid-pop" : ""}`} style={{ background: body, filter: "brightness(1.1)" }}>
        <div className="absolute inset-y-0 left-1/2 -translate-x-1/2 w-3" style={{ background: ribbon }} />
        <div className="absolute -top-5 left-1/2 -translate-x-1/2 flex">
          <span className="w-6 h-6 rounded-full border-[5px] -mr-1 rotate-[-30deg]" style={{ borderColor: ribbon }} />
          <span className="w-6 h-6 rounded-full border-[5px] -ml-1 rotate-[30deg]" style={{ borderColor: ribbon }} />
        </div>
      </div>
    </button>
  );
}

export function GiftBoxes({ premi, onPick, t }) {
  const [chosen, setChosen] = useState(-1);
  const [win, setWin] = useState(null);
  const pick = async (i) => {
    setChosen(i);
    const r = await onPick();
    if (!r) return setChosen(-1);
    setWin(r);
  };
  const others = (premi || []).filter((p) => p !== win?.nome).sort(() => Math.random() - 0.5);
  return (
    <div className="space-y-3" data-testid="gift-boxes">
      <div className="flex justify-center gap-3 sm:gap-6 pt-20">
        {[0, 1, 2].map((i) => {
          const st = chosen < 0 ? "idle" : i === chosen ? (win ? "picked" : "loading") : win ? "other" : "wait";
          const label = !win ? "" : i === chosen ? win.nome : others[(i > chosen ? i - 1 : i) % Math.max(others.length, 1)] || "";
          return <Gift key={i} i={i} state={st === "loading" ? "wait" : st} label={label} onPick={() => pick(i)} />;
        })}
      </div>
      <p className="text-xs text-[#7B929C] h-4">{win ? t("Negli altri pacchi c'era…", "The other boxes held…") : chosen >= 0 ? t("Apriamo…", "Opening…") : t("Tocca un pacco per aprirlo", "Tap a box to open it")}</p>
    </div>
  );
}
